/**
 * Admin Boundaries PMTiles Layer — renders large admin boundary datasets
 * as vector tiles using MapLibre native layers.
 *
 * Used for datasets with >= 2000 features (e.g. Nigeria ADM2 with ~7K LGAs).
 * Smaller datasets continue to use the GeoJSON path for simplicity.
 *
 * PMTiles format: one file per country, ADM levels as named source-layers.
 * Properties preserved: name_en (shapeName), population, area_km2, density_per_km2 (pop_density_km2),
 *   pop_pct_national, parent_iso3 (shapeGroup), shape_type (shapeType).
 */

import { R2_PMTILES_BASE, DESERT_COUNTRIES } from '../config.js';

// 7-level color scheme matching ClassificationEngine YlOrRd
const COLORS_7 = ['#fff0a9', '#fec965', '#fd893c', '#ec3023', '#cd1629', '#af0225', '#800026'];

// Choropleth break presets per metric and admin depth
const CHOROPLETH_STYLES = {
  population: {
    property: 'population',
    breaks: [500000, 2000000, 5000000, 15000000, 50000000, 100000000],
    colors: COLORS_7,
    label: 'Population',
  },
  'population-adm2': {
    property: 'population',
    breaks: [50000, 200000, 500000, 1000000, 5000000, 10000000],
    colors: COLORS_7,
    label: 'Population',
  },
  'population-adm3': {
    property: 'population',
    breaks: [10000, 50000, 200000, 500000, 1000000, 5000000],
    colors: COLORS_7,
    label: 'Population',
  },
  'population-village': {
    property: 'population',
    breaks: [500, 2000, 5000, 15000, 50000, 200000],
    colors: COLORS_7,
    label: 'Population',
  },
  density: {
    property: 'density_per_km2',
    breaks: [10, 50, 200, 500, 1000, 5000],
    colors: COLORS_7,
    label: 'Pop. Density (per km²)',
  },
};

// Extrusion height breakpoints matching the color breaks
// Maps population ranges to heights in meters
const EXTRUSION_PRESETS = {
  population: {
    property: 'population',
    breaks: [500000, 2000000, 5000000, 15000000, 50000000, 100000000],
    heights: [20000, 80000, 200000, 400000, 700000, 1000000, 1200000],
  },
  'population-adm2': {
    property: 'population',
    breaks: [50000, 200000, 500000, 1000000, 5000000, 10000000],
    heights: [20000, 80000, 200000, 400000, 700000, 1000000, 1200000],
  },
  'population-adm3': {
    property: 'population',
    breaks: [10000, 50000, 200000, 500000, 1000000, 5000000],
    heights: [20000, 80000, 200000, 400000, 700000, 1000000, 1200000],
  },
  'population-village': {
    property: 'population',
    breaks: [500, 2000, 5000, 15000, 50000, 200000],
    heights: [20000, 80000, 200000, 400000, 700000, 1000000, 1200000],
  },
  density: {
    property: 'density_per_km2',
    breaks: [10, 50, 200, 500, 1000, 5000],
    heights: [20000, 80000, 200000, 400000, 700000, 1000000, 1200000],
  },
};

/**
 * Manages admin boundary PMTiles layers on the MapLibre globe.
 */
class AdminPMTilesLayer {
  constructor(globe) {
    this.globe = globe;
    this.map = globe.map;
    this._activeSource = null;
    this._activeLayers = new Set();
    this._currentIso3 = null;
    this._currentMode = 'flat'; // 'flat' | 'extruded'
    this._currentState = null;  // { sourceId, admLevel, metric, styleKey, outlineColor }

    // Callbacks for mode switching UI
    this.onModeChange = null;   // (mode) => void
    this.onLegendUpdate = null; // (config) => void
  }

  /**
   * Get the PMTiles URL for a country.
   */
  getUrl(iso3) {
    return `pmtiles://${R2_PMTILES_BASE}/${iso3}.pmtiles`;
  }

  /**
   * Show admin boundaries from PMTiles.
   * @param {string} iso3 - Country ISO3 code
   * @param {string} admLevel - ADM1, ADM2, etc.
   * @param {string} mode - 'outline' or 'choropleth-population', 'choropleth-density'
   * @param {object} [options] - Extra options
   */
  show(iso3, admLevel, mode = 'outline', options = {}) {
    // Remove any existing admin PMTiles layers
    this.clear();

    const sourceId = `admin-pmtiles-${iso3}`;
    const url = this.getUrl(iso3);

    // Add source if not present
    if (!this.map.getSource(sourceId)) {
      this.map.addSource(sourceId, {
        type: 'vector',
        url,
      });
    }

    this._activeSource = sourceId;
    this._currentIso3 = iso3;

    const isDesert = DESERT_COUNTRIES.has(iso3);
    const outlineColor = isDesert ? '#ff1493' : '#ffd200';
    const outlineColorDim = isDesert ? 'rgba(255,20,147,0.55)' : 'rgba(255,210,0,0.55)';

    if (mode === 'outline') {
      this._addOutlineLayers(sourceId, admLevel, outlineColor);
    } else if (mode.startsWith('choropleth-')) {
      const metric = mode.replace('choropleth-', '');
      const styleKey = this._resolveStyleKey(metric, admLevel);

      // Store state for mode switching
      this._currentState = { sourceId, admLevel, metric, styleKey, outlineColor: outlineColorDim };
      this._currentMode = 'flat';

      this._addChoroplethLayers(sourceId, admLevel, styleKey, outlineColorDim);
      this._emitLegend(styleKey);
      this.onModeChange?.('flat');
    }

    console.log(`[AdminPMTiles] Showing ${iso3} ${admLevel} (${mode})`);
  }

  /**
   * Switch between flat and extruded mode (preserves data).
   * @param {'flat'|'extruded'} mode
   */
  setMode(mode) {
    if (!this._currentState || mode === this._currentMode) return;
    const { sourceId, admLevel, styleKey, outlineColor } = this._currentState;
    const prevMode = this._currentMode;
    this._currentMode = mode;

    // Remove existing fill/extrusion layers but keep source
    this._removeLayers();

    if (mode === 'flat') {
      this._addChoroplethLayers(sourceId, admLevel, styleKey, outlineColor);
    } else if (mode === 'extruded') {
      this._addExtrusionLayers(sourceId, admLevel, styleKey, outlineColor);
    }

    // Camera adjustments
    if (mode === 'extruded' && prevMode !== 'extruded') {
      this.map.easeTo({ pitch: 45, duration: 800 });
    } else if (prevMode === 'extruded' && mode !== 'extruded') {
      this.map.easeTo({ pitch: 0, duration: 800 });
    }

    this.onModeChange?.(mode);
  }

  getCurrentMode() { return this._currentMode; }
  hasData() { return this._currentState !== null; }

  /**
   * Add outline-only layers (yellow/pink borders).
   */
  _addOutlineLayers(sourceId, admLevel, color) {
    const lineId = `admin-pt-line-${admLevel}`;

    this.map.addLayer({
      id: lineId,
      type: 'line',
      source: sourceId,
      'source-layer': admLevel,
      paint: {
        'line-color': color,
        'line-width': 1.2,
        'line-opacity': 0.8,
      },
    });

    this._activeLayers.add(lineId);
  }

  /**
   * Add flat choropleth fill + outline layers with data-driven coloring.
   */
  _addChoroplethLayers(sourceId, admLevel, styleKey, outlineColor) {
    const style = CHOROPLETH_STYLES[styleKey] || CHOROPLETH_STYLES.population;
    const fillColorExpr = this._buildStepExpression(style.property, style.breaks, style.colors);

    const fillId = `admin-pt-fill-${admLevel}`;
    const lineId = `admin-pt-line-${admLevel}`;

    this.map.addLayer({
      id: fillId,
      type: 'fill',
      source: sourceId,
      'source-layer': admLevel,
      paint: {
        'fill-color': fillColorExpr,
        'fill-opacity': 0.7,
      },
    });

    this.map.addLayer({
      id: lineId,
      type: 'line',
      source: sourceId,
      'source-layer': admLevel,
      paint: {
        'line-color': outlineColor,
        'line-width': 0.5,
        'line-opacity': 0.6,
      },
    });

    this._activeLayers.add(fillId);
    this._activeLayers.add(lineId);
  }

  /**
   * Add extruded 3D choropleth layers with data-driven coloring and height.
   */
  _addExtrusionLayers(sourceId, admLevel, styleKey, outlineColor) {
    const style = CHOROPLETH_STYLES[styleKey] || CHOROPLETH_STYLES.population;
    const fillColorExpr = this._buildStepExpression(style.property, style.breaks, style.colors);

    const extrusion = EXTRUSION_PRESETS[styleKey] || EXTRUSION_PRESETS.population;
    const heightExpr = this._buildStepExpression(extrusion.property, extrusion.breaks, extrusion.heights);

    const extId = `admin-pt-extrusion-${admLevel}`;
    const lineId = `admin-pt-line-${admLevel}`;

    this.map.addLayer({
      id: extId,
      type: 'fill-extrusion',
      source: sourceId,
      'source-layer': admLevel,
      paint: {
        'fill-extrusion-color': fillColorExpr,
        'fill-extrusion-height': heightExpr,
        'fill-extrusion-base': 0,
        'fill-extrusion-opacity': 0.85,
      },
    });

    this.map.addLayer({
      id: lineId,
      type: 'line',
      source: sourceId,
      'source-layer': admLevel,
      paint: {
        'line-color': outlineColor,
        'line-width': 0.5,
        'line-opacity': 0.4,
      },
    });

    this._activeLayers.add(extId);
    this._activeLayers.add(lineId);
  }

  /**
   * Resolve the appropriate style key based on metric and admin depth.
   */
  _resolveStyleKey(metric, admLevel) {
    const admNum = parseInt(admLevel.replace('ADM', ''));
    if (metric === 'population') {
      if (admNum >= 4) return 'population-village';
      if (admNum >= 3) return 'population-adm3';
      if (admNum >= 2) return 'population-adm2';
      return 'population';
    }
    return metric;
  }

  /**
   * Build a MapLibre 'step' expression for data-driven styling.
   */
  _buildStepExpression(property, breaks, values) {
    const expr = ['step', ['get', property], values[0]];
    for (let i = 0; i < breaks.length; i++) {
      expr.push(breaks[i], values[i + 1]);
    }
    return expr;
  }

  /**
   * Emit a legend config matching the current style.
   */
  _emitLegend(styleKey) {
    const style = CHOROPLETH_STYLES[styleKey] || CHOROPLETH_STYLES.population;
    const { breaks, colors, label } = style;

    const formatNum = (n) => {
      if (n >= 1e9) return (n / 1e9).toFixed(1) + 'B';
      if (n >= 1e6) return (n / 1e6).toFixed(1) + 'M';
      if (n >= 1e3) return (n / 1e3).toFixed(0) + 'K';
      return n.toLocaleString();
    };

    const stops = [];
    // First bracket: < first break
    stops.push({ color: this._hexToRgba(colors[0]), label: `< ${formatNum(breaks[0])}` });
    // Middle brackets
    for (let i = 0; i < breaks.length - 1; i++) {
      stops.push({ color: this._hexToRgba(colors[i + 1]), label: `${formatNum(breaks[i])} – ${formatNum(breaks[i + 1])}` });
    }
    // Last bracket: >= last break
    stops.push({ color: this._hexToRgba(colors[colors.length - 1]), label: `${formatNum(breaks[breaks.length - 1])}+` });

    const config = {
      visible: true,
      title: label,
      type: 'classified',
      stops,
    };
    this.onLegendUpdate?.(config);
  }

  _hexToRgba(hex) {
    const m = hex.replace('#', '').match(/.{2}/g);
    return [parseInt(m[0], 16), parseInt(m[1], 16), parseInt(m[2], 16), 255];
  }

  /**
   * Remove all layers but keep the source.
   */
  _removeLayers() {
    for (const layerId of this._activeLayers) {
      if (this.map.getLayer(layerId)) {
        this.map.removeLayer(layerId);
      }
    }
    this._activeLayers.clear();
  }

  /**
   * Remove all active admin PMTiles layers and sources.
   */
  clear() {
    this._removeLayers();

    if (this._activeSource && this.map.getSource(this._activeSource)) {
      this.map.removeSource(this._activeSource);
    }
    this._activeSource = null;
    this._currentIso3 = null;
    this._currentState = null;
    this._currentMode = 'flat';
  }

  /**
   * Get list of interactive layer IDs (for click popups).
   */
  getInteractiveLayers() {
    return [...this._activeLayers].filter(id => id.includes('-fill-') || id.includes('-extrusion-'));
  }

  /**
   * Check if a PMTiles file exists for a country (by checking manifest).
   */
  static hasPMTiles(manifest, iso3) {
    return !!manifest?.countries?.[iso3]?.pmtiles;
  }
}

export { AdminPMTilesLayer, CHOROPLETH_STYLES };
